import { useCallback } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useBudgetMutations, useBudgetTemplates, type BudgetTemplate } from "hooks/useBudget";
import type { ApplyBudgetTemplateParams } from "hooks/useBudget";
import { Permissions } from "utils/permissions/permissionConstants";
import { useAlertDialog } from "context/AlertDialogContext";
import { handleToast } from "utils";
import { cn } from "utils/cn";
import { BudgetI } from "./types";

interface BudgetTemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  budgetDetail?: Omit<BudgetI, "categories">;
  budgetId?: string;
}

const formatCategoryPreview = (template: BudgetTemplate): string => {
  const names = template.data.map((category) => category.name).filter(Boolean);
  const preview = names.slice(0, 4).join(", ");
  if (names.length > 4) {
    return `${preview}, +${names.length - 4} more`;
  }
  return preview;
};

// Recognizable brand accents: each media company's badge is tinted toward its logo color
// (Netflix/Marvel red, Disney/CBS blue, HBO violet, BBC/AFI mono, Channel 4 teal, etc.).
// Names match the backend template names exactly. Approximations, for at-a-glance scanning only.
// Each tint reads a `hue-*` theme token, so the initials stay legible in every preset and mode.
const ROSE_ACCENT = "bg-hue-rose/15 text-hue-rose border-hue-rose/30";
const VIOLET_ACCENT = "bg-hue-violet/15 text-hue-violet border-hue-violet/30";
const MONO_ACCENT = "bg-muted text-foreground border-border";

const TEMPLATE_BRAND_ACCENTS: Record<string, string> = {
  "Netflix": ROSE_ACCENT,
  "Marvel": ROSE_ACCENT,
  "Telefilm Canada": ROSE_ACCENT,
  "Disney": "bg-hue-indigo/15 text-hue-indigo border-hue-indigo/30",
  "CBS": "bg-hue-blue/15 text-hue-blue border-hue-blue/30",
  "Discovery Networks": "bg-hue-sky/15 text-hue-sky border-hue-sky/30",
  "HBO": VIOLET_ACCENT,
  "BET": VIOLET_ACCENT,
  "UK TV Channel 4": "bg-hue-teal/15 text-hue-teal border-hue-teal/30",
  "BBC": MONO_ACCENT,
  "AFI": MONO_ACCENT,
};

// Stable fallback accent for non-brand templates (tax credits, Animation, Documentary, etc.) so
// the same name always gets the same color. Classes are spelled out so Tailwind keeps them.
const TEMPLATE_ACCENTS = [
  "bg-hue-blue/15 text-hue-blue border-hue-blue/30",
  "bg-hue-emerald/15 text-hue-emerald border-hue-emerald/30",
  "bg-primary/15 text-secondary border-primary/30",
  VIOLET_ACCENT,
  ROSE_ACCENT,
  "bg-hue-cyan/15 text-hue-cyan border-hue-cyan/30",
  ROSE_ACCENT,
  "bg-hue-teal/15 text-hue-teal border-hue-teal/30",
  "bg-hue-indigo/15 text-hue-indigo border-hue-indigo/30",
  "bg-hue-sky/15 text-hue-sky border-hue-sky/30",
] as const;

const getTemplateAccent = (name: string): string => {
  const brandAccent = TEMPLATE_BRAND_ACCENTS[name];
  if (brandAccent) return brandAccent;

  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash + name.charCodeAt(i)) % TEMPLATE_ACCENTS.length;
  }
  return TEMPLATE_ACCENTS[hash];
};

const getTemplateInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
};

const TemplateListLoading = () => (
  <div className="flex flex-col gap-3 py-4">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="h-16 animate-pulse rounded-lg bg-muted/60" />
    ))}
  </div>
);

const TemplateListError = () => (
  <div className="py-8 text-center text-regular-3 text-muted-foreground">
    Unable to load budget templates. Please try again.
  </div>
);

const TemplateListEmpty = () => (
  <div className="py-8 text-center text-regular-3 text-muted-foreground">
    No templates available.
  </div>
);

const TemplateListNeedsScreenplay = () => (
  <div className="py-8 text-center text-regular-3 text-muted-foreground">
    Import your screenplay to start a budget from a template.
  </div>
);

interface TemplateListProps {
  templates: BudgetTemplate[];
  isApplying: boolean;
  onSelect: (template: BudgetTemplate) => void;
}

const TemplateList = ({ templates, isApplying, onSelect }: TemplateListProps) => (
  <div className="flex max-h-[24rem] flex-col gap-2 overflow-y-auto">
    {templates.map((template) => (
      <div
        key={template.name}
        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/50 px-3 py-2.5"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold",
              getTemplateAccent(template.name)
            )}
            aria-hidden
          >
            {getTemplateInitials(template.name)}
          </div>
          <div className="flex min-w-0 flex-col">
            <div className="truncate text-left text-sm font-medium text-foreground">
              {template.name}
            </div>
            <div className="truncate text-left text-xs text-muted-foreground">
              {formatCategoryPreview(template)}
            </div>
          </div>
        </div>
        <Button
          size="xs"
          variant="subtle"
          aria-label="Use template"
          disabled={isApplying}
          onClick={() => onSelect(template)}
        >
          Use
        </Button>
      </div>
    ))}
  </div>
);

export const BudgetTemplatePickerModal = ({
  open,
  onClose,
  budgetDetail,
  budgetId,
}: BudgetTemplatePickerModalProps) => {
  const { projectId, documentId } = useProjectIdentifiers();
  const { canProject, isLoading: isAuthLoading } = useProjectAuth(projectId);
  const { showAlertDialog } = useAlertDialog();

  const {
    data: templates = [],
    isInitialLoading: isTemplatesLoading,
    isError,
  } = useBudgetTemplates({
    projectId: projectId ?? "",
    documentId: documentId ?? "",
    enabled: open,
  });
  // Templates are fetched per document, so a project without a screenplay never requests them.
  // Not the query's `isLoading`: React Query v4 keeps a disabled query in "loading" forever.
  // The request also waits on permissions, so that wait counts as loading.
  const hasScreenplay = Boolean(documentId);
  const isLoading = hasScreenplay && (isAuthLoading || isTemplatesLoading);

  const { applyBudgetTemplate } = useBudgetMutations({
    projectId: projectId ?? "",
    documentId: documentId ?? "",
    budgetId,
  });

  const applyTemplate = useCallback(
    (template: BudgetTemplate) => {
      if (!canProject(Permissions.PROJECT_UPDATE)) {
        handleToast("You do not have permission to update this budget.", "error");
        return;
      }

      const params: ApplyBudgetTemplateParams = {
        categories: template.data,
        budgetId,
        estimatedShootDays: budgetDetail?.estimated_shoot_days,
        contingencyPercentage: budgetDetail?.contingency_percentage,
        bondFeePercentage: budgetDetail?.bond_fee_percentage,
      };

      const runApply = () => {
        applyBudgetTemplate.mutate(params, {
          onSuccess: () => {
            onClose();
          },
        });
      };

      if (budgetId) {
        showAlertDialog({
          title: "Replace current budget?",
          type: "warning",
          message:
            "Applying this template replaces your current account structure and line items. Locked accounts are not preserved.",
          actions: {
            cancel: "Cancel",
            success: {
              text: "Apply template",
              onSuccess: runApply,
            },
          },
        });
        return;
      }

      runApply();
    },
    [
      applyBudgetTemplate,
      budgetDetail?.bond_fee_percentage,
      budgetDetail?.contingency_percentage,
      budgetDetail?.estimated_shoot_days,
      budgetId,
      canProject,
      onClose,
      showAlertDialog,
    ]
  );

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalHeader title="Start from template" onClose={onClose} />
      <ModalBody>
        {hasScreenplay && !isLoading ? (
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-regular-3 text-muted-foreground">{`${templates.length} templates`}</p>
          </div>
        ) : null}
        {!hasScreenplay ? (
          <TemplateListNeedsScreenplay />
        ) : isLoading ? (
          <TemplateListLoading />
        ) : isError ? (
          <TemplateListError />
        ) : templates.length === 0 ? (
          <TemplateListEmpty />
        ) : (
          <TemplateList
            templates={templates}
            isApplying={applyBudgetTemplate.isPending}
            onSelect={applyTemplate}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghostSecondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
